export interface MessageContent {
    role: 'user' | 'assistant';
    content: string | AssistantMessageContent;
    is_final?: boolean;
    temp_message?: string;
    stage?: string;
    traffic?: 'allow' | 'stop';
}

export type ChatMessageRow = {
    _id?: string;
    BatchId: number;
    ConversationId: string;
    Timestamp: string;
    UserId: string;
    content: MessageContent;
    patientId?: string;
    correlationId?: string;
};

export type ChatHistoryResponse = {
    messages: ChatMessageRow[];
    conversation_id?: string;
    conversation_title?: string;
    conversation_full_path?: string;
    patient_id?: string;
    patient_name?: string; // Patient name fetched from OMOP database
    pinned?: boolean;
    last_message_feedback?: {
        message_id: string;
        feedback_type: 'thumbs_up' | 'thumbs_down' | null;
    };
};

// Matches NestJS backend response exactly
export type StartResponse = {
    conversation_id: string;
    correlation_id: string;
    patient_id?: string;
    timestamp: string;
    redirect?: boolean; // True if conversation already exists with same first message
    conversation_title?: string; // Generated from first user message
    is_sample?: boolean; // True if this is a sample query with immediate response
};

// Fork conversation response - includes full metadata for UI cache update
export type ForkConversationResponse = {
    conversation_id: string;
    conversation_title: string;
    timestamp: string;
    is_patient_query: boolean;
    patient_id?: string;
    pinned: boolean;
};

// Conversation list response from GET /conversation endpoint
export type ConversationListResponse = {
    conversation_type: string;
    page: number;
    size: number;
    conversations: FlatConversationNode[];
};

export type QueryTableArgs = {
    raw_query?: string;
    person_id?: string;
    page?: number;
    page_size?: number;
    type?: 'static' | 'dynamic';
    start_date?: string;
    end_date?: string;
    document_id?: string;
    search?: string;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
};

export type QueryTableResponse = {
    records: Record<string, unknown>[];
    total_records: number;
    has_next: boolean;
    page: number;
    page_size: number;
};

export type ChatUIState = {
    isCollapsed: boolean;
    title: string;
    fullPath: string;
    latestDocumentId: string;
    stopTraffic: boolean;
};

export type ChatMessageWithTimeStamp = {
    Timestamp: string;
    content: MessageContent;
};

export interface FlatConversationNode {
    id: string;
    conversation_id?: string;
    title?: string;
    displayName?: string;
    name?: string;
    timestamp?: string;
    items?: FlatConversationNode[];
    patient_id?: string | null;
    patient_name?: string | null; // Patient name fetched from OMOP database
    is_patient_query?: boolean;
    pinned?: boolean; // Pinned flag for flat list architecture
    matched_snippet?: string; // Matched content snippet from search (for display below title)
}

export interface SearchResult extends FlatConversationNode {
    matchType: 'title' | 'content' | 'both';
}

export interface GroupedSearchResults {
    today: SearchResult[];
    yesterday: SearchResult[];
    previous7Days: SearchResult[];
    previous30Days: SearchResult[];
    older: SearchResult[];
}

export interface INodeType {
    id: string;
    items: INodeType[];
    name: string;
    title: string;
    icon: string;
    expanded: boolean;
    conversation_id?: string;
    timestamp?: string;
    owner_id?: string;
    owner_username?: string;
    displayName: string;
    patient_id?: string;
    is_patient_query?: string;
}

export type ChatHistoryRequestType = {
    conversation_id?: string;
    conversation_title?: string;
    flat_view?: boolean;
    page?: number;
    size?: number;
    page_size?: number;
    patient_id?: string;
    conversation_type?: 'shared' | 'owned';
    conversations?: INodeType[];
    start_date?: string;
    end_date?: string;
    search_query?: string;
};

export interface OwnedChatHistoryResponse {
    page: number;
    size: number;
    conversation_id: string;
    conversations: INodeType[];
    conversation_title?: string;
    conversation_full_path?: string;
    patient_id?: string;

    messages: Message[];
    response: {
        assistant_message: Content;
        user_message: string;
        conversation_id: string;
        timestamp: string;
        patient_id?: string;
    };
}

export type Message = {
    ConversationId: string;
    UserId: string;
    Timestamp: string;
    content: {
        startsWith(arg0: string): unknown;
        role: 'assistant' | 'user';
        content: string;
        is_shared: boolean;
    };
    _id: string;
    BatchId: number;
};

export type ConversationItem = {
    id: string;
    name?: string;
    title?: string;
    displayName?: string;
    is_patient_query?: boolean | null;
    patient_id?: string | null;
    timestamp?: string;
    pinned?: boolean;
};

export type Conversations = {
    Today: ConversationItem[];
    Yesterday: ConversationItem[];
    'Last 7 Days': ConversationItem[];
    'Last 30 Days': ConversationItem[];
    'Last year': ConversationItem[];
};

export type SharedConversationItem = {
    conversation_id: string;
    title: string;
    timestamp: string;
    owner_id?: string;
    owner_username?: string;
    displayName?: string;
    name?: string;
    patient_id?: string;
    is_patient_query?: string;
};

export enum ContentType {
    GRID = 'grid',
    CHART = 'chart',
    POPOVERGRID = 'pop_over_grid',
}

export interface Content {
    role: 'user' | 'admin' | 'assistant';
    _id: string;
    sql_query?: {
        raw_query: string;
        table_query: string;
    };
    type: ContentType;
    text: string;
    thought_process?: {
        conditions?: string;
        demographics?: string;
        symptoms?: string;
    };
    traffic?: 'stop' | 'allow';
    temp_message?: string;
    stage: string;
    is_final?: boolean;
    correlation_id?: string;
    query_generation_information?: {
        [key: string]: {
            model: string;
            completion_time: number;
        };
    };
}

export interface SharedChatHistoryResponse {
    page: number;
    size: number;
    conversation_id: string;
    conversations: SharedConversationItem[];
    conversation_title?: string;
    conversation_full_path?: string;
    messages: Message[];
    response: {
        assistant_message: Content;
        user_message: string;
        conversation_id: string;
        timestamp: string;
        patient_id?: string;
    };
}

export type ChatHistoryResponseType =
    | (OwnedChatHistoryResponse & { conversation_type: 'owned' })
    | (SharedChatHistoryResponse & { conversation_type: 'shared' });

export type ChatListProps = {
    onSelect: (conversationId: string, patientId?: string) => void;
    page?: number;
    size?: number;
    conversation_type?: 'owned' | 'shared';
    flat_view?: boolean | 'true' | 'false';
};

export interface CohortPatientTabsProps {
    activeTab: 'cohort' | 'patient';
    setActiveTab: (tab: 'cohort' | 'patient') => void;
    patientId: string;
    setPatientId: (id: string) => void;
}

export type Row = {
    Timestamp: string;
    content: MessageContent;
    _id?: string;
    correlationId?: string;
};

export interface MessageBubbleProps {
    allMessages: Row[];
    isThinking: boolean;
    streamStartedAt: number | null;
    onSqlSelect?: (
        sql: string | null,
        thoughtProcess?: { conditions?: string; demographics?: string; symptoms?: string } | null,
        timestamp?: string,
        messageId?: string
    ) => void;
    onResetToLatest?: () => void;
    onQuestionSelect?: (question: string) => void;
}

export interface TableEditorPaneProps {
    conversationId: string;
    documentId?: string;
    patientId?: string | null;
    initialQuery?: string;
    defaultCohortName?: string;
    createdCohortName?: string;
    showSuccessModal?: boolean;
    isSqlSelected?: boolean;
    setShowSuccessModal?: (val: boolean) => void;
    thoughtProcess?: {
        conditions?: string;
        demographics?: string;
        symptoms?: string;
    } | null;
    /** Message ID for manual selection - used by Explain feature */
    selectedMessageId?: string | null;
}

export interface ShareConversationRequestBody {
    grantee_id: string;
    grant_type: string;
}

export type TimeWindow = '7d' | '30d' | 'all';

// API Response Types - matches NestJS backend
export interface SampleQuestion {
    question: string;
    type: 'cohort' | 'patient-level' | 'cancer';
    description: string;
}

// Backend returns array of sample questions
export interface ConversationSamplesResponse {
    samples: string[];
}

export interface CorrelationStatusResponse {
    correlation_status: boolean;
    user_message?: string;
}

/**
 * Filter confirmation data from QueryBot when multi-filter detection occurs
 */
export interface FilterConfirmationData {
    message: string;
    confirmation_required: boolean;
    intent_type?: 'add' | 'remove' | 'modify';
    previous_filter_count?: number;
}
export type ExplainContent = {
    explanation: string;
    key_insights: string[];
    data_summary: string;
    notable_patterns: string;
    success: boolean;
    error_message: string | null;
};

export interface AssistantMessageContent {
    stage: string;
    text: string;
    sql_query?: string;
    summary_substring?: string; // Substring to highlight on hover
    explanation?: string; // Backend sends this instead of thought_process
    timestamp?: string;
    data?: Record<string, unknown>[] | FilterConfirmationData; // Backend uses 'data' not 'results'

    // Filter confirmation fields (stage: 'filter_confirmation')
    confirmation_required?: boolean;
    status?: string; // 'pending_confirmation' when waiting for user response
    follow_up_questions?: string[];
    explain?: ExplainContent;

    // Legacy fields for backwards compatibility
    is_final?: boolean;
    temp_message?: string;
    sql_queries?: Array<{
        query: string;
        query_id: string;
        execution_date: string;
    }>;
    type?: ContentType;
    thought_process?: {
        conditions?: string;
        demographics?: string;
        symptoms?: string;
    };
    traffic?: 'stop' | 'allow';
    correlation_id?: string;
    query_generation_information?: {
        [key: string]: {
            model: string;
            completion_time: number;
        };
    };
}

// Matches backend QuerybotResponse exactly
export interface StreamPacket {
    response: {
        _id: any;
        user_message: string;
        conversation_id: string;
        patient_id: string | null;
        assistant_message: AssistantMessageContent;
    };
    is_final?: boolean; // Backend sends is_final at top level
}

export interface CreateNodeRequest {
    name: string;
    path?: string;
    is_directory?: boolean;
}

export interface CreateNodeResponse {
    success: boolean;
    node_id?: string;
    message?: string;
}

export interface MoveNodeRequest {
    current_path: string;
    new_path: string;
    is_directory: boolean;
}

export interface MoveNodeResponse {
    success: boolean;
    message?: string;
}

export interface DeleteConversationRequest {
    id: string;
}

export interface DeleteConversationResponse {
    success: boolean;
    message?: string;
}

export interface ShareConversationResponse {
    success: boolean;
    message?: string;
}

/**
 * ES combined message format for patient queries
 * Stores user and assistant messages together in a single document
 */
export interface ESCombinedPatientMessage {
    _id?: string;
    ConversationId: string;
    Timestamp: string;
    UserId: string;
    BatchId: number;
    PatientId?: string | null;
    content: {
        user: {
            role: 'user';
            content: string;
        };
        assistant: {
            role: 'assistant';
            content: AssistantMessageContent;
        };
    };
}

/**
 * Standard ES message format (separate user/assistant messages)
 */
export interface ESStandardMessage {
    _id?: string;
    ConversationId: string;
    Timestamp: string;
    UserId: string;
    BatchId: number;
    PatientId?: string | null;
    content: MessageContent;
}

/**
 * Union type for all ES message formats
 */
export type ESMessage = ESCombinedPatientMessage | ESStandardMessage;

/**
 * Type guard to check if message is combined format
 */
export function isESCombinedMessage(msg: ESMessage): msg is ESCombinedPatientMessage {
    return (
        msg.content &&
        'user' in msg.content &&
        'assistant' in msg.content &&
        typeof (msg.content as any).user === 'object' &&
        typeof (msg.content as any).assistant === 'object'
    );
}

/**
 * Type guard to check if assistant message content requires confirmation
 */
export function isFilterConfirmationMessage(content: AssistantMessageContent | string | undefined): boolean {
    if (!content || typeof content === 'string') return false;
    return content.stage === 'filter_confirmation' && content.confirmation_required === true;
}

/**
 * Type guard to check if data is FilterConfirmationData
 */
export function isFilterConfirmationData(data: unknown): data is FilterConfirmationData {
    return (
        typeof data === 'object' &&
        data !== null &&
        'message' in data &&
        'confirmation_required' in data &&
        typeof (data as FilterConfirmationData).message === 'string'
    );
}

/**
 * Extract confirmation message from assistant content
 */
export function getConfirmationMessage(content: AssistantMessageContent): string | null {
    if (!isFilterConfirmationMessage(content)) return null;

    // Check if message is in the data field (FilterConfirmationData)
    if (content.data && isFilterConfirmationData(content.data)) {
        return content.data.message;
    }

    // Fallback to text field
    return content.text || null;
}

/**
 * Props for the ConfirmationButtons component
 */
export interface ConfirmationButtonsProps {
    /** The confirmation message to display */
    message: string;
    /** Callback when user confirms (Yes) */
    onConfirm: () => Promise<void>;
    /** Callback when user declines (No) */
    onDecline: () => Promise<void>;
    /** Whether the confirmation has already been responded to */
    isResponded?: boolean;
    /** The response that was given (true = Yes, false = No) */
    response?: boolean;
    /** Optional correlation ID for tracking */
    correlationId?: string;
}
